import React, { useEffect, useRef, useState } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';
import LoadingSpinner from '../ui/LoadingSpinner';
import { useCustomers } from '../../hooks/useCustomers';
import type { Customer } from '../../services/customer/customer.types';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

interface MapViewProps {
  refreshKey?: number; // Optional key to force reload
}

const MapView: React.FC<MapViewProps> = ({ refreshKey }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [ready, setReady] = useState(false);
  const { customers, loading, error, reload } = useCustomers();
  const geocodeCacheRef = useRef<Record<string, [number, number]>>({});

  // Small helper moved to component scope to avoid excessive nesting in callbacks.
  const safe = (v: any) => (v === null || v === undefined || v === '' ? '-' : String(v));

  // Reload customers when refreshKey changes
  useEffect(() => {
    if (refreshKey !== undefined && refreshKey > 0) {
      reload();
    }
  }, [refreshKey, reload]);

  // Basic heuristic to derive coordinates (placeholder: center on UK).
  const deriveCoord = (c: Customer, index: number): [number, number] => {
    const baseLng = -1.5597348; // central UK approx
    const baseLat = 52.3887418;
    const spread = 2.5; // degrees
    const angle = (index / Math.max(1, customers.length)) * Math.PI * 2;
    const r = 0.5 + (index % 10) * 0.05;
    return [baseLng + Math.cos(angle) * (r / spread), baseLat + Math.sin(angle) * (r / spread)];
  };

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return; // init once

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [-1.5597348, 52.3887418],
      zoom: 6,
    });

    mapRef.current = map;

    map.on('load', () => {
      map.addControl(new mapboxgl.NavigationControl());

      setReady(true);
    });

    // If page has a fixed header, scrolling can change visible viewport for the map.
    // Call map.resize() on scroll/resize events so Mapbox recalculates layout and marker positions.
    let rafId: number | null = null;
    const onScrollOrResize = () => {
      if (rafId != null) return; // already scheduled
      rafId = globalThis.requestAnimationFrame(() => {
        try {
          map.resize();
        } catch (e) {
          console.info('map resize failed (likely removed):', (e as Error).message || e);
        }
        rafId = null;
      });
    };

    globalThis.addEventListener('scroll', onScrollOrResize, { passive: true });
    globalThis.addEventListener('resize', onScrollOrResize);
    return () => {
      globalThis.removeEventListener('scroll', onScrollOrResize);
      globalThis.removeEventListener('resize', onScrollOrResize);
      if (rafId != null) {
        globalThis.cancelAnimationFrame(rafId);
        rafId = null;
      }
      map.remove();
      mapRef.current = null;
      setReady(false);
    };
  }, []);

  // Helper: geocode a postcode with Mapbox (cache results)
  const geocodePostcode = async (postcode: string): Promise<[number, number] | null> => {
    const code = postcode.trim();
    if (!code) return null;
    const cache = geocodeCacheRef.current;
    if (cache[code]) return cache[code];

    try {
      const q = encodeURIComponent(code);
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${q}.json?access_token=${mapboxgl.accessToken}&limit=1`;
      const res = await fetch(url);
      if (!res.ok) return null;
      const data = await res.json();
      const feat = data?.features?.[0];
      if (feat?.center?.length >= 2) {
        const lng = feat.center[0];
        const lat = feat.center[1];
        cache[code] = [lng, lat];
        return [lng, lat];
      }
    } catch (err) {
      console.info('Geocoding failed for postcode:', code, err);
    }

    return null;
  };

  // Add / refresh markers when customers change and map ready
  useEffect(() => {
    if (!ready || !mapRef.current) return;

    let cancelled = false;

    (async () => {
  // Clear existing markers
  for (const m of markersRef.current) m.remove();
  markersRef.current = [];

      // Resolve coordinates for each customer (prefer explicit coords, then postcode geocode, else heuristic)
      const coords = await Promise.all(customers.map(async (c, idx) => {
        const anyC = c as any;
        if (anyC.longitude != null && anyC.latitude != null) return [anyC.longitude, anyC.latitude] as [number, number];
        if (anyC.lng != null && anyC.lat != null) return [anyC.lng, anyC.lat] as [number, number];
        if (anyC.location && Array.isArray(anyC.location.coordinates)) return anyC.location.coordinates as [number, number];

        if (anyC.postCode) {
          const g = await geocodePostcode(anyC.postCode);
          if (g) return g;
        }

        return deriveCoord(c, idx);
      }));
      for (const [idx, c] of customers.entries()) {
        const el = document.createElement('div');
        el.className = 'marker se-icon--building-marker';
        el.setAttribute('aria-hidden', 'true');

        const [lng, lat] = coords[idx];
        const popupHtml = `
          <div class="map-popup">
            <div class="map-popup__header">
              <h4 class="map-popup__title">${safe((c as any).customerName || (c as any).name || c.id)}</h4>
            </div>
            <div class="map-popup__body">
              <div class="map-popup__row"><span class="map-popup__label">Address 1</span><span class="map-popup__value">${safe((c as any).address1)}</span></div>
              <div class="map-popup__row"><span class="map-popup__label">Town / City</span><span class="map-popup__value">${safe((c as any).townCity)}</span></div>
              <div class="map-popup__row"><span class="map-popup__label">County</span><span class="map-popup__value">${safe((c as any).county)}</span></div>
              <div class="map-popup__row"><span class="map-popup__label">Country</span><span class="map-popup__value">${safe((c as any).country)}</span></div>
              <div class="map-popup__row"><span class="map-popup__label">Post Code</span><span class="map-popup__value">${safe((c as any).postCode)}</span></div>
              <div class="map-popup__row"><span class="map-popup__label">Created</span><span class="map-popup__value">${safe((c as any).createdAt)?.replace('T',' ')}</span></div>
            </div>
          </div>`;
        const popup = new mapboxgl.Popup({ offset: 16, closeButton: true }).setHTML(popupHtml);

        const marker = new mapboxgl.Marker(el)
          .setLngLat([lng, lat])
          .setPopup(popup)
          .addTo(mapRef.current!);

        markersRef.current.push(marker);
      }
    })();

    return () => { cancelled = true; };
  }, [customers, ready]);

    return (
      <div className="map-root">
        <div id="map" ref={containerRef} />
        {loading && (
          <div className="map-overlay map-overlay--loading"><LoadingSpinner message="Loading customers..." /></div>
        )}
        {error && !loading && (
          <div className="map-overlay map-overlay--error">Failed to load customers</div>
        )}
        {!loading && customers.length === 0 && !error && (
          <div className="map-overlay map-overlay--empty">No customers found</div>
        )}
      </div>
    );
};

export default MapView;
